import React from 'react';

/**
 * Component configuration type for config-driven UI
 * Note: This is also defined in componentRegistry.ts for consistency
 */
export interface ComponentConfig {
  component: string;
  props?: Record<string, unknown>;
  children?: (ComponentConfig | string | null | undefined)[];
}

// Direct imports - we'll handle the circular dependency by avoiding renderComponent in the registry itself
import { componentRegistry } from '../components/componentRegistry';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { Typography } from '../components/atoms/Typography';

/**
 * Recursively render components from configuration
 * @param config - Component configuration object, string, or null/undefined
 * @param index - Index for React key prop
 * @param skipErrorComponents - If true, skip failed components; if false, show error message
 * @returns React.ReactNode - Rendered component or null
 */
export const renderComponent = (
  config: ComponentConfig | string | null | undefined,
  index: number,
  skipErrorComponents: boolean = true
): React.ReactNode => {
  // Handle null or undefined
  if (!config) {
    console.warn(
      'renderComponent: Encountered null or undefined config at index',
      index
    );
    return null;
  }

  // If it's a string, just return it as text
  if (typeof config === 'string') {
    return config;
  }

  // Validate config has required properties
  if (!config.component) {
    console.warn(
      'renderComponent: Config missing component property at index',
      index,
      config
    );
    return null;
  }

  const { component, props = {}, children = [] } = config;
  const Component = componentRegistry[component];

  if (!Component) {
    console.warn(
      `renderComponent: Component "${component}" not found in registry. Available components:`,
      Object.keys(componentRegistry)
    );
    return null;
  }

  // Handle special props that need to be rendered as components
  const processedProps = { ...props };

  // If header prop is a ComponentConfig, render it
  if (
    processedProps.header &&
    typeof processedProps.header === 'object' &&
    'component' in processedProps.header
  ) {
    processedProps.header = renderComponent(
      processedProps.header as ComponentConfig,
      0,
      skipErrorComponents
    );
  }

  // If label prop is a ComponentConfig, render it
  if (
    processedProps.label &&
    typeof processedProps.label === 'object' &&
    'component' in processedProps.label
  ) {
    processedProps.label = renderComponent(
      processedProps.label as ComponentConfig,
      0,
      skipErrorComponents
    );
  }

  // If value prop is a ComponentConfig, render it
  if (
    processedProps.value &&
    typeof processedProps.value === 'object' &&
    'component' in processedProps.value
  ) {
    processedProps.value = renderComponent(
      processedProps.value as ComponentConfig,
      0,
      skipErrorComponents
    );
  }

  // Recursively render children, filtering out null values
  const renderedChildren = children
    .map((child, idx) => renderComponent(child, idx, skipErrorComponents))
    .filter((child) => child !== null);

  // Wrap component creation in try-catch to handle creation errors
  try {
    const componentElement = React.createElement(
      Component,
      { key: index, ...processedProps },
      ...renderedChildren
    );

    // Try to wrap with ErrorBoundary if available
    if (ErrorBoundary) {
      const TypographyComponent = Typography;
      const errorFallback = skipErrorComponents
        ? null
        : TypographyComponent
          ? React.createElement(
              TypographyComponent,
              {
                sx: {
                  color: 'error.main',
                  padding: '8px',
                  border: '1px solid',
                  borderColor: 'error.main',
                  borderRadius: '4px',
                  fontSize: '12px',
                },
              },
              `Error in ${component}`
            )
          : React.createElement(
              'div',
              { style: { color: 'red', fontSize: '12px' } },
              `Error in ${component}`
            );

      return React.createElement(
        ErrorBoundary,
        {
          key: `error-boundary-${index}`,
          componentName: component,
          fallback: errorFallback,
        },
        componentElement
      );
    }

    return componentElement;
  } catch (error) {
    console.error(
      `Error creating component "${component}" at index ${index}:`,
      error
    );

    // If skipErrorComponents is true, skip the component (return null)
    if (skipErrorComponents) {
      return null;
    }

    // If skipErrorComponents is false, show error message
    if (Typography) {
      return React.createElement(
        Typography,
        {
          key: index,
          sx: {
            color: 'error.main',
            padding: '8px',
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: '4px',
            fontSize: '12px',
          },
        },
        `Error creating ${component}`
      );
    }

    // Fallback to basic div if Typography not available
    return React.createElement(
      'div',
      {
        key: index,
        style: {
          color: 'red',
          padding: '8px',
          border: '1px solid red',
          borderRadius: '4px',
          fontSize: '12px',
        },
      },
      `Error creating ${component}`
    );
  }
};
